import { useRouter } from 'next/router';
import usePrimeLibrary from '@api/hooks/prime/usePrimeLibrary';
import { useSetSelectedNav } from '@stores/sidenav';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import {
  PlaylistValue,
  SpecialClassesResponse,
  SubCategoriesResponse,
  LiveNowResponse,
  SheldonLibraryResponse
} from '@api/fetchers/prime/types';
import { getIsLoggedIn } from '@api/selectors/auth';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { UserPlatformPlan } from '@stores/prime-quota/types';
import Carousel from '@comp/Carousel/Carousel';
import PrimeContentCard, {
  PrimeContentCardVariants
} from 'aquilla/core/organisms/PrimeContentCard';
import usePrimeVideoQuota from '@hooks/usePrimeVideoQuota';
import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@stores/AppCommonData';
import TopEducatorPlaylistCard from 'aquilla/core/organisms/TopEducatorPlaylistCard';
import { formatDuration, getSlug } from '@utils/prime';
import { Spacings } from '@styles/index';
import usePrimeTrialPromoModalStore from '@stores/prime-pitch/store';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import usePrimeHomeStore from '@stores/prime-home';
import {
  sheldonItemToCardProps,
  playlistItemToEducatorCard,
  formatCompact,
  formatViews
} from './PrimeHome.helpers';
import {
  BottomSpacer,
  PrimeCardWrap,
  StyledLiveClassPrime,
  PrimeTrendingCard
} from './PrimeHome.styles';
import { PrimeHomeSkeletons, ContentShimmer } from './PrimeHome.skeletons';
import FAQSection from './FAQSection';
import PrimeBottomBanner from './PrimeBottomBanner';
import PrimeHomePills from './PrimeHomePills';
import PrimeHomeMobileHeader from './PrimeHomeMobileHeader';
import usePrimeHomeHandlers from './usePrimeHomeHandlers';

export interface PrimeHomeProps {
  specialClasses: SpecialClassesResponse | null;
  subCategories: SubCategoriesResponse;
  liveNow: LiveNowResponse | null;
  sheldon: SheldonLibraryResponse | null;
}

const getGoalUIDFromQuery = (query: Record<string, any>) => {
  const goalUID = query?.goalUID;
  return Array.isArray(goalUID) ? goalUID[0] : goalUID;
};

const getGoalSlugFromQuery = (query: Record<string, any>) => {
  const goalSlug = query?.goalSlug;
  return Array.isArray(goalSlug) ? goalSlug[0] : goalSlug;
};

const byRank = (a: { rank: number }, b: { rank: number }) => a.rank - b.rank;

const PrimeHome = ({
  specialClasses,
  subCategories,
  liveNow,
  sheldon
}: PrimeHomeProps) => {
  const router = useRouter();
  const { selectedGoal } = useSelectedGoal();
  const goalUID = getGoalUIDFromQuery(router.query);
  const goalSlug = getGoalSlugFromQuery(router.query);
  useSetSelectedNav(plusNavBarItems.PRIME);
  const { isQuotaExhausted, unlockedVideosUids, primeVideoQuota } =
    usePrimeVideoQuota(goalUID);
  const openPrimeTrialPromoModal = usePrimeTrialPromoModalStore(
    (state) => state.openModal
  );
  const videosLeft = primeVideoQuota?.left ?? 0;
  const isPlusUser = useMyInfo(getIsSubscribedToGoal(goalUID));
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const hasPrimePaidPlan =
    usePrimeQuotaStore((state) => state.userPlatformPlan) ===
    UserPlatformPlan.PAID;
  const shouldShowBottomBanner = isLoggedIn && !isPlusUser && !hasPrimePaidPlan;
  const sortedSubCategories = React.useMemo(() => {
    if (!subCategories || subCategories.length === 0) return [];
    return [...subCategories].sort(byRank);
  }, [subCategories]);

  const [selectedSubCategory, setSelectedSubCategory] = useState(
    sortedSubCategories?.[0]?.uid || ''
  );
  const selectedCategoryUID =
    subCategories?.find(
      (subCategory) => subCategory.uid === selectedSubCategory
    )?.parentUid ||
    subCategories?.[0]?.parentUid ||
    '';
  const setSelectedPrimeCategory = usePrimeHomeStore(
    (state) => state.setSelectedPrimeCategory
  );
  const setPrimeBreadcrumbs = usePrimeHomeStore(
    (state) => state.setPrimeBreadcrumbs
  );
  const isMobile = useIsMobile();
  const [subHeaderEl, setSubHeaderEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSelectedPrimeCategory({
      selectedSubCategoryUID: selectedSubCategory,
      selectedCategoryUID
    });
  }, [selectedSubCategory, selectedCategoryUID, setSelectedPrimeCategory]);
  useEffect(() => {
    const el = document.getElementById('layout-sub-header');
    if (el) {
      setSubHeaderEl(el);
      return;
    }
    const observer = new MutationObserver(() => {
      const target = document.getElementById('layout-sub-header');
      if (target) {
        setSubHeaderEl(target);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  const { data: sheldonData, isLoading: sheldonLoading } =
    usePrimeLibrary(selectedSubCategory);
  const activeSheldon = sheldonData || sheldon;
  const isInitialLoading =
    !sheldon &&
    !sheldonData &&
    (!sortedSubCategories || sortedSubCategories.length === 0) &&
    sheldonLoading;
  const showContentSkeleton =
    sheldonLoading && !sheldonData && !isInitialLoading;
  const rankOneSections = activeSheldon?.filter((s) => s.rank === 1) || [];
  const otherSections = activeSheldon?.filter((s) => s.rank !== 1) || [];
  const selectedSubCategoryMeta = subCategories?.find(
    (category) => category.uid === selectedSubCategory
  );

  const {
    handleCardClick,
    trackViewAllClicked,
    setPrimeSectionBreadcrumb,
    handleSearchClick
  } = usePrimeHomeHandlers({
    selectedSubCategory,
    selectedCategoryUID,
    selectedSubCategoryMeta,
    sortedSubCategories,
    selectedGoal,
    goalUID,
    goalSlug,
    isQuotaExhausted,
    unlockedVideosUids
  });

  const { allSections, trendingSectionUid } = React.useMemo(() => {
    if (!activeSheldon || activeSheldon.length === 0) {
      return { allSections: [], trendingSectionUid: null };
    }
    const sorted = [...activeSheldon].sort(byRank);
    const firstSection = sorted[0];
    const shouldTrend =
      firstSection?.nestingType === 1 && firstSection?.items?.length > 0;
    const sectionsWithSortedItems = sorted.map((section) => ({
      ...section,
      items: section.items ? [...section.items].sort(byRank) : []
    }));

    return {
      allSections: sectionsWithSortedItems,
      trendingSectionUid: shouldTrend ? firstSection.uid : null
    };
  }, [activeSheldon]);

  if (isInitialLoading) {
    return <PrimeHomeSkeletons />;
  }
  return (
    <>
      {!isMobile && (
        <PrimeHomePills
          subCategories={sortedSubCategories}
          selectedSubCategory={selectedSubCategory}
          onSelectSubCategory={setSelectedSubCategory}
          subHeaderEl={subHeaderEl}
        />
      )}
      {isMobile && (
        <PrimeHomeMobileHeader
          subCategories={sortedSubCategories}
          selectedSubCategory={selectedSubCategory}
          onSelectSubCategory={setSelectedSubCategory}
          onSearchClick={handleSearchClick}
        />
      )}
      {showContentSkeleton ? (
        <ContentShimmer />
      ) : (
        <>
          {liveNow && liveNow.results.length > 0 && (
            <Carousel
              isPrime
              numberOfItems={1}
              showInfo={false}
              autoScroll
              showScrollDots
              showBottomGap={false}
              mobileItemMinWidth="100%"
              itemGridColumn="span 18"
            >
              {liveNow.results.map((sc) => (
                <StyledLiveClassPrime
                  key={sc.uid}
                  imageUrl={sc.coverPhotoV1}
                  title={sc.name}
                  buttonLabel="Watch Now"
                  isLocked={
                    isQuotaExhausted &&
                    !!sc.uid &&
                    !unlockedVideosUids.includes(sc.uid)
                  }
                  onButtonClick={() => handleCardClick(sc, 'Live-Now', '')}
                />
              ))}
            </Carousel>
          )}
          {allSections
            .map((section) => {
              if (!section.items?.length) return null;
              const isTrendingSection = section.uid === trendingSectionUid;
              if (isTrendingSection) {
                return (
                  <Carousel
                    isPrime
                    showBottomGap={false}
                    key={section.uid}
                    label={section.name}
                    numberOfItems={4}
                    mobileItemMinWidth="140px"
                    minItems={1}
                    allowOverflowPreview
                    itemGridColumn="span 4"
                    gridColumnGap={Spacings.SPACING_7B}
                  >
                    {section.items.map((item) => {
                      return (
                        <PrimeTrendingCard
                          key={item.value.uid}
                          imageUrl={item.value.thumbnail}
                          isLocked={
                            isQuotaExhausted &&
                            !!item.value.uid &&
                            !unlockedVideosUids.includes(item.value.uid)
                          }
                          rank={item.rank}
                          onClick={() =>
                            handleCardClick(
                              item,
                              section.name,
                              section.uid,
                              section.rank
                            )
                          }
                        />
                      );
                    })}
                  </Carousel>
                );
              }
              return null;
            })
            .filter(Boolean)}
          {specialClasses?.results?.length > 0 && (
            <Carousel
              isPrime
              showBottomGap={false}
              label="Watch special classes"
              numberOfItems={3}
              mobileItemMinWidth="160px"
              seeAllLink={`/prime/${selectedSubCategory}/${getSlug(
                'Watch special classes'
              )}/special-classes`}
              href={`/prime/${selectedSubCategory}/${getSlug(
                'Watch special classes'
              )}/special-classes`}
              handleClickSeeAll={() =>
                trackViewAllClicked({
                  playlistName: 'Watch special classes',
                  playlistType: 'VIDEO',
                  lastPrimarySourceSection: 'VIDEO'
                })
              }
            >
              {specialClasses.results.map((sc) => (
                <PrimeContentCard
                  key={sc.uid}
                  isLocked={
                    isQuotaExhausted &&
                    sc.uid &&
                    !unlockedVideosUids.includes(
                      sc.nextSession?.properties?.uid
                    )
                  }
                  title={sc.name}
                  isSpecial
                  showTopDecorations={false}
                  thumbnailUrl={sc.coverPhotoV1 || sc.coverPhoto || undefined}
                  variant={PrimeContentCardVariants.MostViewed}
                  specialFooterTopLine={formatCompact(
                    sc.startsAt,
                    sc.endsAt,
                    sc.isLive === true
                  )}
                  footerText={`${formatViews(
                    sc.nextSession?.properties?.attendance
                  )} views`}
                  as="button"
                  onClick={() =>
                    handleCardClick(sc, 'Watch-special-classes', '')
                  }
                />
              ))}
            </Carousel>
          )}
          {allSections.map((section) => {
            if (!section.items?.length) return null;
            if (section.uid === trendingSectionUid) return null;
            if (section.nestingType === 3) {
              return (
                <Carousel
                  isPrime
                  key={section.uid}
                  label={section.name}
                  minItems={1}
                  showScrollDots
                  autoScroll
                  showbottomGap
                  gridColumnGap={Spacings.SPACING_4B}
                  mobileItemMinWidth="330px"
                >
                  {section.items.map((item) => {
                    const isLocked =
                      isQuotaExhausted &&
                      !!item.value.uid &&
                      !unlockedVideosUids.includes(item.value.uid);
                    const cardProps = playlistItemToEducatorCard(
                      item,
                      isLocked
                    );
                    return (
                      <TopEducatorPlaylistCard
                        key={item.value.uid}
                        heroImageUrl={cardProps.heroImageUrl}
                        lessons={cardProps.lessons}
                        variant={cardProps.variant}
                        onViewAllClick={() => {
                          setPrimeSectionBreadcrumb(section.name, section.rank);
                          trackViewAllClicked({
                            playlistName: item.value.name,
                            playlistUid: item.value.uid,
                            playlistRank: item.rank,
                            playlistNestingType: String(section.nestingType),
                            playlistType: 'PLAYLIST',
                            lastPrimarySourceSection: 'PLAYLIST'
                          });
                          router.push(
                            `/prime/${selectedSubCategory}/${getSlug(
                              section.name
                            )}/${item.value.uid}/all`
                          );
                        }}
                        ariaLabel={cardProps.ariaLabel}
                        onLessonClick={(lesson) => {
                          const playlist = item.value as PlaylistValue;
                          const nestedItem = (playlist.items || []).find(
                            (nested) => nested.value.uid === lesson.id
                          );
                          if (nestedItem) {
                            handleCardClick(
                              nestedItem,
                              section.name,
                              item.value.uid,
                              section.rank,
                              item.value.name
                            );
                          }
                        }}
                      />
                    );
                  })}
                </Carousel>
              );
            }
            return (
              <Carousel
                isPrime
                showBottomGap={false}
                key={section.uid}
                label={section.name}
                mobileItemMinWidth="160px"
                numberOfItems={3}
                seeAllLink={`/prime/${selectedSubCategory}/${getSlug(
                  section.name
                )}/${section.uid}/all`}
                href={`/prime/${selectedSubCategory}/${getSlug(section.name)}/${
                  section.uid
                }/all`}
                handleClickSeeAll={() => {
                  setPrimeSectionBreadcrumb(section.name, section.rank);
                  trackViewAllClicked({
                    playlistName: section.name,
                    playlistUid: section.uid,
                    playlistRank: section.rank,
                    playlistNestingType: String(section.nestingType),
                    playlistType: 'PLAYLIST',
                    lastPrimarySourceSection: 'PLAYLIST'
                  });
                }}
              >
                {section.items.map((item) => {
                  const cardProps = sheldonItemToCardProps(item);
                  return (
                    <PrimeCardWrap key={item.value.uid}>
                      <PrimeContentCard
                        showTopDecorations={item.contentType === 'playlist'}
                        title={cardProps.title}
                        footerText={
                          item.contentType === 'playlist'
                            ? item.value.itemCount +
                              (item.value.itemCount > 1 ? ' videos' : ' video')
                            : formatDuration(item.value.duration)
                        }
                        isLocked={
                          item.contentType === 'playlist'
                            ? false
                            : isQuotaExhausted &&
                              !!item.value.uid &&
                              !unlockedVideosUids.includes(item.value.uid)
                        }
                        variant={
                          item.contentType === 'playlist'
                            ? PrimeContentCardVariants.Default
                            : PrimeContentCardVariants.PopularInSubject
                        }
                        thumbnailUrl={cardProps.thumbnailUrl}
                        isSpecial={cardProps.isSpecial}
                        as="button"
                        onClick={() =>
                          handleCardClick(
                            item,
                            section.name,
                            section.uid,
                            section.rank
                          )
                        }
                      />
                    </PrimeCardWrap>
                  );
                })}
              </Carousel>
            );
          })}
        </>
      )}
      <FAQSection />
      {shouldShowBottomBanner && <BottomSpacer />}
      {shouldShowBottomBanner && (
        <PrimeBottomBanner
          title={
            videosLeft <= 0
              ? 'Daily limit reached'
              : `${videosLeft} free ${
                  videosLeft === 1 ? 'video' : 'videos'
                } available for today!`
          }
          subtitle="Get unlimited videos with Prime."
          contextualId={PrimeTrialPromoContextualId.VIDEO}
          goalUID={goalUID}
          goalSlug={goalSlug}
        />
      )}
    </>
  );
};
export default PrimeHome;
